import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";

type Stats = {
  totalUsers: number | null;
  openReports: number | null;
  flaggedUsers: number | null;
  activeRooms: number | null;
  recentReports: RecentReport[];
  /** Soru havuzu sağlığı — oyunun kendi durumu, moderasyondan bağımsız. */
  weakCategories: number | null;
  totalAnswers: number | null;
};

const MIN_PER_LETTER = 5;
const HEALTHY_LETTERS = 10;
const TR_LETTERS = "abcçdefgğhıijklmnoöprsştuüvyz".split("");

function playableCount(words: string[]): number {
  const counts = new Map<string, number>();
  for (const w of words) {
    const c = w[0];
    if (c) counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return TR_LETTERS.filter((l) => (counts.get(l) ?? 0) >= MIN_PER_LETTER).length;
}

type RecentReport = {
  id: string;
  targetName: string;
  reason: string;
  createdAtMs: number;
};

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: null,
    openReports: null,
    flaggedUsers: null,
    activeRooms: null,
    recentReports: [],
    weakCategories: null,
    totalAnswers: null,
  });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [usersCount, openReportsCount, flaggedCount, activeRoomsCount] =
          await Promise.all([
            getCountFromServer(collection(db, "users")),
            getCountFromServer(
              query(collection(db, "reports"), where("status", "==", "open"))
            ),
            getCountFromServer(
              collection(db, "moderation", "flagged", "users")
            ).catch(() => ({ data: () => ({ count: 0 }) } as any)),
            getCountFromServer(
              query(collection(db, "rooms"), where("status", "in", [
                "waiting",
                "playing",
              ]))
            ),
          ]);

        const recentSnap = await getDocs(
          query(
            collection(db, "reports"),
            where("status", "==", "open"),
            orderBy("createdAt", "desc"),
            limit(5)
          )
        );
        const recentReports: RecentReport[] = recentSnap.docs.map((d) => {
          const data = d.data();
          const ts = data.createdAt?.toMillis?.() ?? Date.now();
          return {
            id: d.id,
            targetName: (data.targetName as string) ?? "—",
            reason: (data.reason as string) ?? "—",
            createdAtMs: ts,
          };
        });

        // Soru havuzu: kategori sayısı az (31) olduğu için tamamını çekip
        // zayıfları burada saymak, ayrı bir özet belgesi tutmaktan basit.
        let weakCategories: number | null = null;
        let totalAnswers: number | null = null;
        try {
          const catSnap = await getDocs(collection(db, "categories"));
          let weak = 0;
          let total = 0;
          catSnap.docs.forEach((d) => {
            const words = (d.data().words as string[]) ?? [];
            total += words.length;
            if (playableCount(words) < HEALTHY_LETTERS) weak++;
          });
          weakCategories = weak;
          totalAnswers = total;
        } catch {
          // Havuz henüz aktarılmamış olabilir — kart boş kalsın, sayfa çalışsın.
        }

        setStats({
          totalUsers: usersCount.data().count,
          openReports: openReportsCount.data().count,
          flaggedUsers: flaggedCount.data().count,
          activeRooms: activeRoomsCount.data().count,
          recentReports,
          weakCategories,
          totalAnswers,
        });
      } catch (e) {
        setErr("Veri çekilemedi: " + (e as Error).message);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Anlık moderasyon ve oyun sağlığı özeti"
      />

      {err && (
        <div className="rounded-xl border border-danger/40 bg-danger/10 text-danger px-4 py-3 text-sm font-semibold mb-4">
          {err}
        </div>
      )}

      {/* Altı kart: 3'lü iki sıra, 4'lü olunca ikinci sıra yarım kalıyordu. */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatTile
          label="Toplam kullanıcı"
          value={fmt(stats.totalUsers)}
          tone="neutral"
        />
        <StatTile
          label="Açık şikayet"
          value={fmt(stats.openReports)}
          tone={stats.openReports && stats.openReports > 0 ? "warn" : "good"}
        />
        <StatTile
          label="İşaretli oyuncu"
          value={fmt(stats.flaggedUsers)}
          tone={stats.flaggedUsers && stats.flaggedUsers > 0 ? "bad" : "good"}
        />
        <StatTile
          label="Aktif oda"
          value={fmt(stats.activeRooms)}
          tone="good"
          hint="waiting + playing"
        />
        <StatTile
          label="Toplam cevap"
          value={fmt(stats.totalAnswers)}
          hint="soru havuzundaki kelime"
        />
        <StatTile
          label="Zayıf kategori"
          value={fmt(stats.weakCategories)}
          tone={
            stats.weakCategories === null
              ? "neutral"
              : stats.weakCategories > 0
              ? "warn"
              : "good"
          }
          hint={`${HEALTHY_LETTERS} harften az oynanan`}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-extrabold">Son şikayetler</h2>
            <Link
              to="/admin/reports"
              className="text-xs font-bold text-primarySoft hover:underline"
            >
              Hepsini gör →
            </Link>
          </div>
          {stats.recentReports.length === 0 ? (
            <div className="text-sm text-textMuted">Açık şikayet yok. 🎉</div>
          ) : (
            <ul className="divide-y divide-stroke/70 -mx-2">
              {stats.recentReports.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-2 py-3">
                  <div className="h-9 w-9 rounded-full bg-danger/20 border border-danger/30 grid place-items-center text-sm">
                    🚩
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">
                      {r.targetName}
                    </div>
                    <div className="text-xs text-textMuted">
                      {translateReason(r.reason)} · {timeAgo(r.createdAtMs)}
                    </div>
                  </div>
                  <Link
                    to="/admin/reports"
                    className="text-xs font-bold text-primarySoft"
                  >
                    İncele
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-5">
          <h2 className="font-extrabold mb-4">Hızlı işlemler</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/admin/reports" className="btn-outline justify-start">
              🚩 Şikayet kuyruğu
            </Link>
            <Link to="/admin/flagged" className="btn-outline justify-start">
              ⚠️ İşaretliler
            </Link>
            <Link to="/admin/users" className="btn-outline justify-start">
              👥 Kullanıcı ara
            </Link>
            <Link to="/admin/rooms" className="btn-outline justify-start">
              🎮 Canlı odalar
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function fmt(n: number | null) {
  return n === null ? "…" : n.toLocaleString("tr-TR");
}

function translateReason(r: string) {
  switch (r) {
    case "inappropriateName":
      return "Uygunsuz kullanıcı adı";
    case "offensiveBehavior":
      return "Saldırgan davranış";
    case "cheating":
      return "Hile";
    case "spam":
      return "Spam / bot";
    case "other":
      return "Diğer";
    default:
      return r;
  }
}

function timeAgo(ms: number) {
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  return `${day} gün önce`;
}
